import React from 'react';
import { Gift, Trophy, ChevronRight, Heart, Clock, Calendar, Flame, Sparkles } from 'lucide-react';
import { SmartNudge } from '../hooks/useStatsData';
interface CardSmartNudgesProps {
  loading: boolean;
  nudges: SmartNudge[] | null;
}
export const CardSmartNudges: React.FC<CardSmartNudgesProps> = ({
  loading,
  nudges
}) => {
  if (loading) return <div className="arcane-tablet bg-gradient-to-b from-indigo-50/90 to-purple-50/90 dark:from-[#0f0c29] dark:to-[#24243e] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm h-full animate-pulse" data-id="element-21034">
        <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded-md w-1/3 mb-4" data-id="element-21035"></div>
        <div className="space-y-3" data-id="element-21036">
          <div className="h-24 bg-gray-100 dark:bg-gray-900 rounded-lg" data-id="element-21037"></div>
          <div className="h-24 bg-gray-100 dark:bg-gray-900 rounded-lg" data-id="element-21038"></div>
          <div className="h-24 bg-gray-100 dark:bg-gray-900 rounded-lg" data-id="element-21039"></div>
        </div>
      </div>;
  if (!nudges || nudges.length === 0) return <div className="arcane-tablet bg-gradient-to-b from-indigo-50/90 to-purple-50/90 dark:from-[#0f0c29] dark:to-[#24243e] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm h-full" data-id="element-21040">
        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200 mb-4" data-id="element-21041">
          <Sparkles className="text-purple-600 dark:text-pink-400" size={18} data-id="element-21042" />
          Smart Nudges
        </h3>
        <div className="flex items-center justify-center h-40" data-id="element-21043">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-21044">
            No smart nudges available.
          </p>
        </div>
      </div>;
  // Helper function to get nudge icon and color
  const getNudgeInfo = (type: string) => {
    switch (type) {
      case 'lootbox':
        return {
          icon: <Gift size={20} data-id="element-21045" />,
          color: 'from-purple-500 to-pink-500 dark:from-purple-600 dark:to-pink-600',
          border: 'border-purple-300/50 dark:border-purple-500/30',
          shadow: 'shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:shadow-[0_0_15px_rgba(168,85,247,0.2)]'
        };
      case 'gift':
        return {
          icon: <Heart size={20} data-id="element-21046" />,
          color: 'from-pink-500 to-red-500 dark:from-pink-600 dark:to-red-600',
          border: 'border-pink-300/50 dark:border-pink-500/30',
          shadow: 'shadow-[0_0_15px_rgba(236,72,153,0.1)] dark:shadow-[0_0_15px_rgba(236,72,153,0.2)]'
        };
      case 'streak':
        return {
          icon: <Flame size={20} data-id="element-21047" />,
          color: 'from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600',
          border: 'border-amber-300/50 dark:border-amber-500/30',
          shadow: 'shadow-[0_0_15px_rgba(251,191,36,0.1)] dark:shadow-[0_0_15px_rgba(251,191,36,0.2)]'
        };
      default:
        return {
          icon: <Trophy size={20} data-id="element-21048" />,
          color: 'from-blue-500 to-cyan-500 dark:from-blue-600 dark:to-cyan-600',
          border: 'border-blue-300/50 dark:border-blue-500/30',
          shadow: 'shadow-[0_0_15px_rgba(59,130,246,0.1)] dark:shadow-[0_0_15px_rgba(59,130,246,0.2)]'
        };
    }
  };
  return <div className="arcane-tablet bg-gradient-to-b from-indigo-50/90 to-purple-50/90 dark:from-[#0f0c29] dark:to-[#24243e] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm h-full relative overflow-hidden" data-id="element-21049">
      {/* Mystical particle background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-21050">
        {Array.from({
        length: 15
      }).map((_, i) => <div key={`particle-${i}`} className="absolute w-1 h-1 rounded-full bg-purple-500/20 dark:bg-purple-400/10 animate-float-particle" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${Math.random() * 10 + 10}s`
      }} data-id="element-21051"></div>)}
      </div>
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10" data-id="element-21052">
        <svg width="100%" height="100%" className="absolute inset-0" data-id="element-21053">
          <pattern id="nudge-pattern" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-21054">
            <path d="M20 5 L35 20 L20 35 L5 20 Z" fill="none" stroke="currentColor" strokeWidth="0.5" data-id="element-21055" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#nudge-pattern)" data-id="element-21056" />
        </svg>
      </div>
      {/* Glowing inner border */}
      <div className="absolute inset-0 rounded-lg border border-purple-300/20 dark:border-indigo-500/20 shadow-[inset_0_0_20px_rgba(168,85,247,0.05)] dark:shadow-[inset_0_0_30px_rgba(138,43,226,0.1)] pointer-events-none" data-id="element-21057"></div>
      <div className="relative" data-id="element-21058">
        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200 mb-4" data-id="element-21059">
          <Sparkles className="text-purple-600 dark:text-pink-400" size={18} data-id="element-21060" />
          Smart Nudges
        </h3>
        <div className="space-y-3" data-id="element-21061">
          {nudges.map((nudge, index) => {
          const {
            icon,
            color,
            border,
            shadow
          } = getNudgeInfo(nudge.type);
          return <div key={index} className={`bg-white/70 dark:bg-gray-900/30 rounded-lg border ${border} backdrop-blur-sm overflow-hidden relative group hover:${shadow} transition-all duration-300`} data-id="element-21062">
                {/* Subtle glyph background pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none" data-id="element-21063">
                  <svg width="100%" height="100%" data-id="element-21064">
                    <pattern id={`glyph-pattern-${index}`} width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-21065">
                      <path d="M10 2 L18 10 L10 18 L2 10 Z" fill="none" stroke="currentColor" strokeWidth="0.5" data-id="element-21066" />
                    </pattern>
                    <rect width="100%" height="100%" fill={`url(#glyph-pattern-${index})`} data-id="element-21067" />
                  </svg>
                </div>
                {/* Glowing border effect on hover */}
                <div className="absolute inset-0 border border-transparent dark:border-purple-500/0 group-hover:border-purple-300/50 dark:group-hover:border-purple-500/30 rounded-lg transition-all duration-300 group-hover:shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:group-hover:shadow-[0_0_15px_rgba(168,85,247,0.2)]" data-id="element-21068"></div>
                <div className="p-3" data-id="element-21069">
                  <div className="flex items-start gap-3" data-id="element-21070">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r ${color} text-white flex-shrink-0 shadow-[0_0_10px_rgba(168,85,247,0.1)] dark:shadow-[0_0_10px_rgba(168,85,247,0.2)]`} data-id="element-21071">
                      {icon}
                    </div>
                    <div className="flex-1" data-id="element-21072">
                      <h4 className="font-medium text-gray-800 dark:text-indigo-200" data-id="element-21073">
                        {nudge.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-indigo-200/70 mt-1" data-id="element-21074">
                        {nudge.description}
                      </p>
                      <button className={`mt-2 w-full py-1.5 bg-gradient-to-r ${color} text-white rounded-lg text-xs font-medium hover:shadow-md transition-all flex items-center justify-center gap-1 relative overflow-hidden group`} onClick={nudge.action} data-id="element-21075">
                        {/* Button hover glow effect */}
                        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-21076"></div>
                        <span className="relative z-10" data-id="element-21077">
                          {nudge.actionText}
                        </span>
                        <ChevronRight size={14} className="relative z-10" data-id="element-21078" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
        <p className="text-xs text-gray-500 dark:text-indigo-200/70 mt-4" data-id="element-21079">
          Smart Nudges help you maximize your rewards and unlock new
          experiences.
        </p>
      </div>
    </div>;
};